import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
// @mui
import { useTheme, styled } from '@mui/material/styles';
import { Box, Card, CardHeader, Skeleton } from '@mui/material';
// utils
import { fNumber } from '../../../utils/formatNumber';
// components
import { useChart } from '../../../components/chart';
import i18n from '../../../i18n';
import PieChartEmpty from '../../../components/charts/placeholders/PieChartEmpty';

// ----------------------------------------------------------------------

const CHART_HEIGHT = 372;
const LEGEND_HEIGHT = 82;

const StyledChartWrapper = styled('div')(({ theme }) => ({
    "height": CHART_HEIGHT,
    "marginTop": theme.spacing(5),
    '& .apexcharts-canvas svg': { height: CHART_HEIGHT },
    '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
        overflow: 'visible'
    },
    '& .apexcharts-legend': {
        height: LEGEND_HEIGHT,
        alignContent: 'center',
        position: 'relative !important',
        borderTop: `solid 1px ${theme.palette.divider}`,
        top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`
    }
}));

// ----------------------------------------------------------------------

AppCurrentVisits.propTypes = {
    title: PropTypes.string,
    subheader: PropTypes.string,
    chartColors: PropTypes.arrayOf(PropTypes.string),
    chartData: PropTypes.array
};

export default function AppCurrentVisits({
    title,
    subheader,
    chartColors,
    chartData,
    loading,
    ...other
}) {
    const theme = useTheme();

    const chartLabels = chartData.map((i) => i18n.t(`dashboard.${i.label}`));

    const chartSeries = chartData.map((i) => i.value);  

    const chartOptions = useChart({
        colors: chartColors,
        labels: chartLabels,
        stroke: { colors: [theme.palette.background.paper] },
        legend: { floating: true, horizontalAlign: 'center' },
        dataLabels: { enabled: true, dropShadow: { enabled: false } },
        tooltip: {
            fillSeriesColor: false,
            y: {
                formatter: (seriesName) => fNumber(seriesName),
                title: {
                    formatter: (seriesName) => `${seriesName}`
                }
            }
        },
        plotOptions: {
            pie: { donut: { labels: { show: false } } }
        }
    });
    const total = chartSeries.reduce((acc, v) => acc + v, 0);
    const isEmpty = !loading && (chartSeries.length === 0 || total <= 0);

    return (
        <Card {...other}>
            <CardHeader
                title={
                    loading ? <Skeleton variant="text" width={180} /> : title
                }
                subheader={
                    loading && subheader ? (
                        <Skeleton variant="text" width={120} />
                    ) : (
                        subheader
                    )
                }
            />

            <StyledChartWrapper dir="ltr">
                {loading ? (
                    <Box
                        sx={{
                            height: 280,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2
                        }}
                    >
                        <Skeleton
                            variant="circular"
                            width={200}
                            height={200}
                            animation="wave"
                        />
                    </Box>
                ) : isEmpty ? (
                    <PieChartEmpty
                        height={280}
                        title={i18n.t('common.noData')}
                    />
                ) : (
                    <ReactApexChart
                        type="pie"
                        series={chartSeries}
                        options={chartOptions}
                        height={280}
                    />
                )}

                {loading && (
                    <Box
                        sx={{
                            height: LEGEND_HEIGHT,
                            borderTop: (t) => `solid 1px ${t.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2,
                            gap: 1
                        }}
                    >
                        <Skeleton
                            variant="text"
                            width={90}
                            height={18}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            width={70}
                            height={18}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            width={80}
                            height={18}
                            animation="wave"
                        />
                    </Box>
                )}
            </StyledChartWrapper>
        </Card>
    );
}
